package com.siriusxm.pia.views.news

import androidx.compose.runtime.*
import com.siriusxm.pia.Application
import com.siriusxm.unifiedcontent.cmc.CMCService
import com.siriusxm.unifiedcontent.cmc.CreateNewsItemPayload
import com.siriusxm.unifiedcontent.cmc.NewsItem
import com.siriusxm.unifiedcontent.cmc.UpdateNewsItemPayload
import io.ktor.client.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.serialization.kotlinx.json.*
import io.ktor.utils.io.CancellationException
import kotlinx.browser.localStorage
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlin.js.Date

/**
 * Global state manager for news items.
 *
 * This singleton manages news items displayed on the home page and news feed.
 * It includes client-side caching with a 1-hour expiry to reduce API calls
 * for the home page display.
 *
 * Features:
 * - Caches latest news item in localStorage with timestamp
 * - Automatically refreshes cache after 1 hour
 * - Supports force refresh for immediate updates after editing
 * - Full CRUD operations for news items
 *
 * Usage:
 * ```kotlin
 * @Composable
 * fun MyComponent() {
 *     val news = rememberNews()
 *     val latestItem = news.latestItem.value
 *
 *     if (news.isAdmin) {
 *         Button(onClick = { news.createItem("Title", "Content") }) {
 *             Text("Add News")
 *         }
 *     }
 * }
 * ```
 */
class NewsState {
    private var cmcService: CMCService? = null
    private val json = Json {
        isLenient = true
        ignoreUnknownKeys = true
    }

    companion object {
        /** localStorage key for cached latest news item */
        private const val CACHE_KEY = "cmc_news_latest"

        /** localStorage key for cache timestamp */
        private const val CACHE_TIMESTAMP_KEY = "cmc_news_timestamp"

        /** Cache duration in milliseconds (1 hour) */
        private const val CACHE_DURATION_MS = 60 * 60 * 1000

        /** Singleton instance of NewsState */
        val instance = NewsState()
    }

    /**
     * The most recent news item (used for home page display).
     */
    val latestItem = mutableStateOf<NewsItem?>(null)

    /**
     * All loaded news items (used for the full news page).
     */
    val items = mutableStateOf<List<NewsItem>>(emptyList())

    /**
     * Token for loading next page of items.
     */
    val nextToken = mutableStateOf<String?>(null)

    /**
     * Whether news items are currently being loaded.
     */
    val isLoading = mutableStateOf(false)

    /**
     * Whether the news state has been initialized at least once.
     */
    val isInitialized = mutableStateOf(false)

    /**
     * Initializes the news state with the CMC service client.
     * Should be called once when the application starts.
     *
     * @param cmcApiUrl The base URL for the CMC API
     * @param installAuth Function to install authentication on the HTTP client
     */
    fun initialize(cmcApiUrl: String, installAuth: (HttpClientConfig<*>) -> Unit) {
        if (cmcService != null) return

        val httpClient = HttpClient {
            install(ContentNegotiation) {
                json(Json {
                    isLenient = true
                    ignoreUnknownKeys = true
                })
            }
            expectSuccess = true
            installAuth(this)
        }
        cmcService = CMCService(cmcApiUrl, httpClient)
    }

    /**
     * Checks if the cache is still valid (less than 1 hour old).
     *
     * @return true if the cache is valid, false otherwise
     */
    private fun isCacheValid(): Boolean {
        val timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY)?.toDoubleOrNull() ?: return false
        return (Date.now() - timestamp) < CACHE_DURATION_MS
    }

    /**
     * Loads the cached latest news item from localStorage.
     */
    private fun loadFromCache(): Boolean {
        val cached = localStorage.getItem(CACHE_KEY) ?: return false
        return try {
            latestItem.value = json.decodeFromString<NewsItem>(cached)
            true
        } catch (e: Exception) {
            false
        }
    }

    /**
     * Saves the latest news item to localStorage cache.
     */
    private fun saveToCache(item: NewsItem?) {
        if (item != null) {
            localStorage.setItem(CACHE_KEY, json.encodeToString(item))
            localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString())
        } else {
            clearCache()
        }
    }

    /**
     * Clears the cache to force a refresh on next load.
     */
    fun clearCache() {
        localStorage.removeItem(CACHE_KEY)
        localStorage.removeItem(CACHE_TIMESTAMP_KEY)
    }

    /**
     * Loads just the latest news item (for home page display).
     * Uses caching to reduce API calls.
     *
     * @param forceRefresh If true, bypasses cache and fetches from server
     */
    suspend fun loadLatestItem(forceRefresh: Boolean = false) {
        val service = cmcService ?: return

        // Check cache first (unless force refresh)
        if (!forceRefresh && isCacheValid() && loadFromCache()) {
            isInitialized.value = true
            return
        }

        isLoading.value = true
        try {
            val result = service.news.listNewsItems(1, null)
            latestItem.value = result.items?.firstOrNull()
            saveToCache(latestItem.value)
            isInitialized.value = true
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            console.error("Failed to load latest news item", e)
            // Try to load from cache as fallback even if expired
            loadFromCache()
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Loads all news items (for full news page).
     * Does not use caching - always fetches from server.
     *
     * @param refresh If true, clears existing items and starts from beginning
     */
    suspend fun loadAllItems(refresh: Boolean = false) {
        val service = cmcService ?: return

        if (refresh) {
            items.value = emptyList()
            nextToken.value = null
        }

        isLoading.value = true
        try {
            val result = service.news.listNewsItems(20, nextToken.value)
            val newItems = result.items ?: emptyList()
            items.value = if (refresh || nextToken.value == null) {
                newItems
            } else {
                items.value + newItems
            }
            nextToken.value = result.nextToken

            // Also update latestItem if this is a fresh load
            if (refresh || latestItem.value == null) {
                latestItem.value = items.value.firstOrNull()
                saveToCache(latestItem.value)
            }
            isInitialized.value = true
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            console.error("Failed to load news items", e)
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Creates a new news item.
     * Only admins can create news items.
     *
     * @param markdown The markdown content
     * @return The created news item, or null if creation failed
     */
    suspend fun createItem(markdown: String): NewsItem? {
        val service = cmcService ?: return null

        try {
            val item = service.news.createNewsItem(CreateNewsItemPayload(markdown))
            // Add to beginning of list (newest first)
            items.value = listOf(item) + items.value
            // Update latest item and cache
            latestItem.value = item
            saveToCache(item)
            return item
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            console.error("Failed to create news item", e)
            return null
        }
    }

    /**
     * Updates an existing news item.
     * Only admins can update news items.
     *
     * @param id The ID of the item to update
     * @param markdown The new markdown content (optional)
     * @return The updated news item, or null if update failed
     */
    suspend fun updateItem(id: String, markdown: String?): NewsItem? {
        val service = cmcService ?: return null

        try {
            val item = service.news.updateNewsItem(id, UpdateNewsItemPayload(markdown))
            // Update in list
            items.value = items.value.map { if (it.id == id) item else it }
            // Update latest item if it was the one updated
            if (latestItem.value?.id == id) {
                latestItem.value = item
                saveToCache(item)
            }
            return item
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            console.error("Failed to update news item", e)
            return null
        }
    }

    /**
     * Deletes a news item.
     * Only admins can delete news items.
     *
     * @param id The ID of the item to delete
     * @return true if deletion succeeded, false otherwise
     */
    suspend fun deleteItem(id: String): Boolean {
        val service = cmcService ?: return false

        try {
            service.news.deleteNewsItem(id)
            // Remove from list
            items.value = items.value.filter { it.id != id }
            // Update latest item if it was deleted
            if (latestItem.value?.id == id) {
                latestItem.value = items.value.firstOrNull()
                saveToCache(latestItem.value)
            }
            return true
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            console.error("Failed to delete news item", e)
            return false
        }
    }
}

/**
 * CompositionLocal providing access to the NewsState.
 */
val LocalNewsState: ProvidableCompositionLocal<NewsState> =
    staticCompositionLocalOf { NewsState.instance }

/**
 * Composable function to access the news state.
 * Automatically initializes and loads the latest item on first access.
 *
 * @return The NewsState instance
 */
@Composable
fun rememberNews(): NewsState {
    val state = LocalNewsState.current

    // Initialize and load latest news if not already done
    LaunchedEffect(Unit) {
        val cmcApiUrl = Application.configuration.cmcApiUrl
        if (cmcApiUrl != null && !state.isInitialized.value) {
            state.initialize(cmcApiUrl) { config ->
                Application.context.installAuth(config)
            }
            state.loadLatestItem()
        }
    }

    return state
}
